import { combineEpics, ofType } from 'redux-observable';
import { of, concat } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { reduce } from 'lodash';

import { convertDate } from '../../../utility/dates';

import {
  PROVIDER_SURGE_INCENTIVE_CREATE_FORM,
  PROVIDER_SURGE_INCENTIVE_EDIT_FORM,
  PROVIDER_ACTIVE_SURGE_INCENTIVE_EDIT,
  PROVIDER_UPCOMING_SURGE_INCENTIVE_EDIT,
  PROVIDER_SURGE_INCENTIVE_EDIT,
  PROVIDER_SURGE_INCENTIVE_SEARCH_FORM
} from '../action-types';
import {
  providerSurgeIncentiveCreateForm,
  providerSurgeIncentiveEditForm,
  providerActiveSurgeIncentiveEdit,
  providerUpcomingSurgeIncentiveEdit,
  providerSurgeIncentiveEdit,
  providerSurgeIncentiveCreateRequest,
  providerSurgeIncentiveUpdateSearchFilters,
  providerSurgeIncentiveEditRequest
} from '../actions';
import ProviderSurgeIncentiveStatus from '../enums/provider-surge-incentive-status';

import { getFormErrors } from '../utilities/validations/provider-surge-incentive-validator';

export const searchSurgeIncentiveSubmitEpic = (action$, state$) =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_SEARCH_FORM.SUBMIT),
    map(() => {
      const {
        formValues: { state: state_code, 'active-only': active, 'starting-after': start_date }
      } = state$.value.providerSurgeIncentive.searchForm;

      const filters = reduce(
        {
          state_code,
          active,
          start_date: convertDate(start_date, { keepLocalTime: true }),
          page: 1
        },
        (result, value, key) => ({
          ...result,
          [key]: value || undefined
        }),
        {}
      );

      return providerSurgeIncentiveUpdateSearchFilters(filters);
    }));

export const createSurgeIncentiveSubmitEpic = (action$, state$) =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_CREATE_FORM.SUBMIT),
    switchMap(() => {
      const {
        formValues: { 'start-date': startDate, 'end-date': endDate, amount, states }
      } = state$.value.providerSurgeIncentive.createForm;

      const errorMessages = getFormErrors({ startDate, endDate, amount, states });

      if (errorMessages.length > 0) {
        return of(providerSurgeIncentiveCreateForm.setFormErrors(errorMessages));
      }

      return concat(
        of(providerSurgeIncentiveCreateForm.setFormErrors([])),
        of(
          providerSurgeIncentiveCreateRequest.start({
            startDate: convertDate(startDate, { keepLocalTime: true }),
            endDate: convertDate(endDate, { keepLocalTime: true }),
            amount,
            states
          })
        )
      );
    }));

export const editSurgeIncentiveSubmitEpic = (action$, state$) =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_EDIT_FORM.SUBMIT),
    map(() => {
      const {
        formValues: { status, id, 'start-date': startDate, 'end-date': endDate, amount }
      } = state$.value.providerSurgeIncentive.editForm;

      if (status === ProviderSurgeIncentiveStatus.ACTIVE) {
        return providerActiveSurgeIncentiveEdit({
          id,
          endDate,
          amount
        });
      }

      return providerUpcomingSurgeIncentiveEdit({
        id,
        startDate,
        endDate,
        amount
      });
    }));

export const editActiveSurgeIncentiveEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_ACTIVE_SURGE_INCENTIVE_EDIT),
    map(action => {
      const {
        surgeIncentive: { id, endDate, amount }
      } = action.payload;

      const errorMessages = getFormErrors({ endDate, amount }, { useDefaultDate: true });

      if (errorMessages.length > 0) {
        return providerSurgeIncentiveEditForm.setFormErrors(errorMessages);
      }

      return providerSurgeIncentiveEdit({
        id,
        endDate,
        amount
      });
    }));

export const editUpcomingSurgeIncentiveEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_UPCOMING_SURGE_INCENTIVE_EDIT),
    map(action => {
      const {
        surgeIncentive: { id, startDate, endDate, amount }
      } = action.payload;

      const errorMessages = getFormErrors({ startDate, endDate, amount });

      if (errorMessages.length > 0) {
        return providerSurgeIncentiveEditForm.setFormErrors(errorMessages);
      }

      return providerSurgeIncentiveEdit({
        id,
        startDate,
        endDate,
        amount
      });
    }));

export const editSurgeIncentiveEpic = (action$, state$) =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_EDIT),
    switchMap(action => {
      const { id, startDate, endDate, amount } = action.payload.surgeIncentive;

      const currentSurgeIncentive = state$.value.providerSurgeIncentive.incentives.byId[id];
      if (
        +amount === +currentSurgeIncentive.amount &&
        endDate.isSame(currentSurgeIncentive.end_date) &&
        (!startDate || startDate.isSame(currentSurgeIncentive.start_date))
      ) {
        return of(
          providerSurgeIncentiveEditForm.setFormErrors(['You have not made any changes.'])
        );
      }

      return concat(
        of(providerSurgeIncentiveEditForm.setFormErrors([])),
        of(
          providerSurgeIncentiveEditRequest.start({
            id,
            startDate: convertDate(startDate, { keepLocalTime: true }),
            endDate: convertDate(endDate, { keepLocalTime: true }),
            amount
          })
        )
      );
    }));

export default combineEpics(
  searchSurgeIncentiveSubmitEpic,
  createSurgeIncentiveSubmitEpic,
  editSurgeIncentiveSubmitEpic,
  editActiveSurgeIncentiveEpic,
  editUpcomingSurgeIncentiveEpic,
  editSurgeIncentiveEpic
);
